import type {
    ProductDetail,
    ProductExportResponse,
    ProductExportRow,
    ProductListItem,
    ProductListResponse,
    ProductAmountRange,
    ProductVariant,
} from '@/api/models';
import { percentNumberToBps, tetriFromDecimal } from '@/utils';

/**
 * Wire → domain for product money (BOS-162).
 *
 * The products endpoints send decimal GEL. Prices gain a `*Tetri` twin here, at
 * the one edge that crosses, so the table and the detail panel format integers.
 * The decimals stay as the wire's echo; nothing downstream should compute on
 * them.
 */

const tetriOfNullable = (value: number | null): number | null =>
    value === null ? null : tetriFromDecimal(value);

const toAmountRange = (wire: ProductAmountRange): ProductAmountRange => ({
    ...wire,
    minTetri: tetriFromDecimal(wire.min),
    maxTetri: tetriFromDecimal(wire.max),
});

const toVariant = (wire: ProductVariant): ProductVariant => ({
    ...wire,
    // A server before API-195 sends no alternates and no draft fields; they
    // read as none and not a draft.
    alternateBarcodes: wire.alternateBarcodes ?? [],
    salePriceTetri: tetriFromDecimal(wire.salePrice),
    discountPriceTetri: tetriOfNullable(wire.discountPrice),
    wholesalePriceTetri: tetriOfNullable(wire.wholesalePrice),
    currentCostTetri: tetriOfNullable(wire.currentCost),
    batches: (wire.batches ?? []).map((batch) => ({
        ...batch,
        purchasePriceTetri: tetriFromDecimal(batch.purchasePrice),
    })),
});

export const toProductListItem = (wire: ProductListItem): ProductListItem => ({
    ...wire,
    priceRange: toAmountRange(wire.priceRange),
    // A server from before BOS-171 sends no range at all. That reads as
    // unknown, like a product with no stock, never as zero.
    costRange: wire.costRange ? toAmountRange(wire.costRange) : null,
    draft: wire.draft ?? false,
});

export const toProductListResponse = (
    wire: ProductListResponse,
): ProductListResponse => ({
    ...wire,
    items: (wire.items ?? []).map(toProductListItem),
    // A server from before BOS-172 sends no catalogue count. The toolbar then
    // shows the plain count rather than "33 of 0".
    catalogCount: wire.catalogCount ?? null,
});

export const toProductDetail = (wire: ProductDetail): ProductDetail => ({
    ...wire,
    variants: (wire.variants ?? []).map(toVariant),
    draft: wire.draft ?? false,
    draftIssues: wire.draftIssues ?? [],
});

/**
 * An export row's money gains its tetri twins and the markup its basis
 * points (bos-app#120), so the workbook writes numbers from exact integers.
 */
const toProductExportRow = (wire: ProductExportRow): ProductExportRow => ({
    ...wire,
    categoryPath: wire.categoryPath ?? [],
    optionValues: wire.optionValues ?? [],
    alternateBarcodes: wire.alternateBarcodes ?? [],
    draft: wire.draft ?? false,
    currentCostTetri: tetriOfNullable(wire.currentCost),
    salePriceTetri: tetriFromDecimal(wire.salePrice),
    discountPriceTetri: tetriOfNullable(wire.discountPrice),
    wholesalePriceTetri: tetriOfNullable(wire.wholesalePrice),
    markupBps:
        wire.markupPercent === null
            ? null
            : percentNumberToBps(wire.markupPercent),
});

export const toProductExportResponse = (
    wire: ProductExportResponse,
): ProductExportResponse => ({
    rows: (wire.rows ?? []).map(toProductExportRow),
});
